import React from 'react';
import { Search, X } from 'lucide-react';

interface CommonSearchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  containerClassName?: string;
  inputSize?: 'xs' | 'sm' | 'md' | 'lg';
}

import { useTranslation } from 'react-i18next';

const CommonSearch = React.forwardRef<HTMLInputElement, CommonSearchProps>(({
  value,
  onChange,
  containerClassName = '',
  inputSize = 'md',
  ...rest
}, ref) => {
  const { t } = useTranslation();
  const searchInputId = React.useId();
  const placeholderText = rest.placeholder || t('system.streetSegments.filters.searchPlaceholder');
  const handleClear = () => {
    onChange('');
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const sizeClasses = {
    xs: 'py-1.5 text-xs',
    sm: 'py-1.5 text-sm',
    md: 'py-2 text-sm',
    lg: 'py-2.5 text-base',
  };

  return (
    <div className={`relative w-full ${containerClassName}`} role="search">
      <label htmlFor={searchInputId} className="sr-only">
        {typeof placeholderText === 'string' ? placeholderText : t('global.search')}
      </label>
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <Search className="h-4 w-4 text-text-muted" strokeWidth={2.33} />
      </div>
      <input
        id={searchInputId}
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full pl-10 pr-10 border border-border-strong rounded-[4px] bg-surface text-text font-sans font-normal not-italic leading-none tracking-normal focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary placeholder:font-normal placeholder:text-sm placeholder:leading-none placeholder:tracking-normal placeholder:text-text-subtle ${sizeClasses[inputSize]}`}
        placeholder={placeholderText}
        aria-label={typeof placeholderText === 'string' ? placeholderText : t('global.search')}
        {...rest}
      />
      {value && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <button
            type="button"
            onMouseDown={handleMouseDown}
            onClick={handleClear}
            className="p-1 rounded-full focus:outline-none"
            aria-label="Clear search"
          >
            <X className="h-4 w-4 text-text-muted" strokeWidth={2.33}/>
          </button>
        </div>
      )}
    </div>
  );
});

CommonSearch.displayName = 'CommonSearch';

export default React.memo(CommonSearch);